import React from "react";
import {Navigate, Route, Routes, useLocation} from 'react-router';
import Logout from "./components/Logout/Logout";
import ProjectPlan from "./pages/ProjectPlan";
import ProjectPlanDetail from "./pages/ProjectPlanDetail";
import {SessionContext} from "./util/SessionContext";
import MyCres from "./pages/MyCres";
import ChangeOrderList from "./pages/ChangeOrderList";
import ChangeOrderDetail from "./pages/ChangeOrderDetail";
import DocumentImports from "./pages/DocumentImports";
import DocumentImportDetail from "./pages/DocumentImportDetail";
import ObjectApprovalMatrixPage from "./pages/ObjectApprovalMatrixPage";
import UnitsOfMeasurePage from "./pages/UnitsOfMeasurePage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ObjectRoute from "./pages/ObjectRoute";
import FormDesignerPage from './pages/FormDesignerPage';
import RecordFormDesignerPage from './pages/RecordFormDesignerPage';
import Home from "./pages/Home";
import Capabilities from "./pages/Capabilities";
import CapabilityExample from "./pages/CapabilityExample";
import Dashboard from "./pages/Dashboard";
import Inbox from "./pages/Inbox";
import ObjectList from "./pages/ObjectList";
import WorkStreams from "./pages/WorkStreams";
import FoldersPage from "./pages/FoldersPage";
import ObjectDetail from "./pages/ObjectDetail";
import Documentation from "./pages/Documentation";
import QuizBuilder from "./pages/QuizBuilder";
import MyTraining from "./pages/MyTraining";
import TrainingMetrics from "./pages/TrainingMetrics";
import {OBJECT_CLASSIFICATIONS} from "./util/objectClassifications";

export default function AppRoutes() {
    const location = useLocation();

    const sessionContext = new SessionContext();
    const loggedIn = sessionContext.valid() && location.pathname !== "/logout";

    let content;

    if (loggedIn) {
        if (sessionContext.getURL() !== null && sessionContext.getURL()!.includes("projectPlanUSID") && sessionContext.getURL()!.includes("projectPlanPhaseUSID")) {
            content = (
                <ProjectPlanDetail />
            )
        } else {
            content = (
                <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/logout" element={<Logout />} />
                    {/* WHERE THE NOTIFICATION EMAILS POINT. A plain URL carrying no token: the
                        handoff into whichever application the notification concerns is minted
                        here, at click time, from a session that already exists. */}
                    <Route path="/inbox" element={<Inbox />} />
                    <Route path="/project-plan" element={<ProjectPlan />} />
                    {/* EVERY DEMAND'S PATH FROM REQUEST TO BUILD, one stream per quote request. */}
                    <Route path="/work-streams" element={<WorkStreams />} />
                    <Route path="/folders" element={<FoldersPage />} />
                    <Route path="/folders/:usid" element={<FoldersPage />} />
                    {/* One list page and one detail page serve every classification; the registry
                        supplies the labels, icon and route for each.

                        FORM TAKES THE LIST AND NOT THE DETAIL. A form's record page is the designer:
                        opening one to read its Schema as raw JSON on the generic page would be
                        technically correct and useless. The list, the filters, the counts and the
                        persona scoping are all wanted exactly as they are. */}
                    {OBJECT_CLASSIFICATIONS.flatMap(c => [
                        // KEYED ON THE CLASSIFICATION, AND THAT KEY IS LOAD-BEARING.
                        //
                        // Every one of these routes renders the SAME component type at the same
                        // place in the tree, so React reconciles them as one instance and merely
                        // swaps the prop: moving from Items to Build Plans kept every piece of
                        // ObjectList's state. The pane went on offering the Item questions -- and
                        // worse than the display, a ticked Item TYPE or form value stayed
                        // APPLIED, so the Build Plans query went out filtered by
                        // `ObjectTypeAcronym IN ('CAN-')` and returned nothing, with the pane
                        // giving no clue why. Ed: "I am on build plans and the attribute to filter
                        // by are for items."
                        //
                        // A KEY RATHER THAN A RESET EFFECT, because a reset has to enumerate the
                        // state it clears -- seven pieces today: statuses, types, facets, the
                        // available/truncated pair, chosen facet values, facet ranges -- and the
                        // eighth would be forgotten. Remounting cannot forget one. It is also the
                        // move ObjectList already makes on the applet inside it.
                        <Route key={c.route} path={c.route}
                               element={<ObjectList key={c.code} classification={c} />} />,
                        ...(c.code === "FORM" ? [] : [
                            // NOT KEYED, deliberately. The same reconciliation applies here --
                            // one component for every classification and every record -- but
                            // ObjectDetail loads from the route's usid and has been doing so
                            // correctly; keying it would remount on every record change and throw
                            // away work nobody asked me to touch. Worth a look on its own.
                            <Route key={`${c.route}/:usid`} path={`${c.route}/:usid`}
                                   element={<ObjectDetail classification={c} />} />,
                        ]),
                    ])}
                    <Route path="/forms/:usid" element={<FormDesignerPage />} />
                    {/* The form of ONE record, designed by its owners. Keys on the revision, like the record. */}
                    <Route path="/object/:usid/form-design" element={<RecordFormDesignerPage />} />
                    {/* The list and one change order. The detail route keys on the change order's
                        own USID -- unlike a record, whose route keys on a REVISION, because a
                        change order has no revisions. */}
                    <Route path="/change-orders" element={<ChangeOrderList />} />
                    <Route path="/change-orders/:usid" element={<ChangeOrderDetail />} />
                    {/* The import queue and one import. A document import is NOT a species --
                        it is a child table by §2.1's test -- so it gets none of the generic list
                        and detail routes every species has, and without these two the human
                        confirmation that promotion depends on is unreachable by the human it
                        depends on. The detail route keys on the IMPORT's own USID: an import has
                        no revisions, like a change order. */}
                    <Route path="/document-imports" element={<DocumentImports />} />
                    <Route path="/document-imports/:usid" element={<DocumentImportDetail />} />
                    {/* THE PROJECT CONTAINER, which is NOT /project-plan above: that family is
                        phases, milestones and the equipment reserved against them. This is the
                        commercial folder, and they share the word and nothing else. */}
                    <Route path="/object-approval-matrix" element={<ObjectApprovalMatrixPage />} />
                    <Route path="/units-of-measure" element={<UnitsOfMeasurePage />} />
                    <Route path="/approvals" element={<ApprovalsPage />} />
                    {/* The link shape the server writes; ObjectRoute sends it on to the
                        classification route that serves the record. */}
                    <Route path="/object/:usid" element={<ObjectRoute />} />
                    <Route path="/mycres" element={<MyCres />} />
                    {/* The index of the user guides. The guides themselves are static files under
                        public/docs and are served by the web server, not by a route. */}
                    <Route path="/documentation" element={<Documentation />} />
                    {/* What the product does, with a worked example per capability and the bill of
                        materials beside six other products. Readable signed out too, below. */}
                    <Route path="/capabilities" element={<Capabilities />} />
                    <Route path="/capabilities/:slug" element={<CapabilityExample />} />
                    {/* The Quiz Builder. Keyed on the REVISION, like the record routes: a quiz belongs
                        to one revision and is frozen when that revision is released. A page rather
                        than a dialog because authoring a quiz is work, and because "Back To Document"
                        is only a truthful offer if there is somewhere to go back from. */}
                    <Route path="/quiz/:usid" element={<QuizBuilder />} />
                    {/* What the signed-in person owes. Not keyed on anything: it is always about the
                        caller, which is why the server computes it from their own memberships. */}
                    <Route path="/my-training" element={<MyTraining />} />
                    <Route path="/training-metrics" element={<TrainingMetrics />} />
                    <Route path="/project-plan/project-plan-detail" element={<ProjectPlanDetail />} />
                    <Route path="/project-plan/project-plan-detail/:projectPlanUSID/:projectPlanPhaseUSID" element={<ProjectPlanDetail />} />


                    <Route path="/*" element={<Navigate to="/" replace state={{
                        referrer: {
                            pathname: location.pathname,
                            search: location.search,
                        },
                    }} />} />
                </Routes>
            )
        }
    }

    if (!loggedIn) {
        if (location.search.includes("projectPlanUSID") && location.search.includes("projectPlanPhaseUSID")) {
            sessionContext.setURL(location.pathname + location.search);
        }
        content = (
            <Routes>
                <Route path="/logout" element={<Logout />} />
                {/* READABLE WITHOUT SIGNING IN, like t4's: the reader is somebody deciding whether
                    to evaluate this, and the splash links here. */}
                <Route path="/capabilities" element={<Capabilities />} />
                <Route path="/capabilities/:slug" element={<CapabilityExample />} />
                <Route path="/*" element={<Home />} />
            </Routes>
        )
    }
    return (
        content
    );
}
